import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { RegionDto, RegionsStore } from '../../../stores/configuration/locations/regions-store';
import { nameof } from '../../../utils/object';
import { OrderDefinition } from 'stores/dataStore';
import { useCallback, useEffect, useState } from 'react';
import TailwindMultiSelect from 'widgets/editors/tailwind-multiselect';

interface TailwindMultipleRegionEditorProps {
  value: RegionDto[];
  onChange?: (value: RegionDto[]) => void;
  regions?: RegionsStore;
  placeholder?: string;
  clearable?: boolean;
  className?: string;
  regionsReceived?: string[];
  loadDataOnOpen?: boolean;
  disabled?: boolean;
  onBlur?: (event: any) => void;
  onFocus?: (event: any) => void;
}

const TailwindMultipleRegionEditor: React.FC<TailwindMultipleRegionEditorProps> = ({
  value,
  onChange,
  regions,
  placeholder,
  clearable,
  className,
  regionsReceived,
  disabled = false,
  loadDataOnOpen = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<RegionDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getRegionsMethod = useCallback(async () => {
    try {
      setIsLoading(true);
      if (options.length <= 0) {
        let filter;
        const orderBy: OrderDefinition[] = [{ direction: 'Ascending', field: nameof<RegionDto>('name'), useProfile: false }];

        if (regionsReceived && regionsReceived.length > 0) filter = [{ id: { in: { type: 'guid', value: regionsReceived } } }];

        const result = await regions.getAllAsync({ searchQuery: '', skip: 0, take: 100000, orderBy, filter });

        if (result) {
          setOptions(result.items || []);
        }
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [options.length, regions, regionsReceived]);

  const handleChange = (newValue: RegionDto[]) => {
    if (onChange) {
      const selectedRegions = newValue.map(v => options.find(opt => opt.id === v.id)).filter(Boolean);
      onChange(selectedRegions);
    }
  };

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      getRegionsMethod();
    }
  }, [loadDataOnOpen, options.length, getRegionsMethod]);

  useEffect(() => {
    if (!loadDataOnOpen) {
      getRegionsMethod();
    }
  }, [loadDataOnOpen, getRegionsMethod]);

  return (
    <TailwindMultiSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(region => ({
              id: region.id,
              text: region.name
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={value?.map(v => ({
        id: v.id,
        text: v.name
      }))}
      onChange={handleChange}
      placeholder={placeholder || t('Select location')}
      clearable={clearable}
      className={`w-full ${className}`}
      onBlur={onBlur}
      onFocus={onFocus}
      searchable={!isLoading}
      disabled={disabled}
      onOpenDropDown={onOpenDropDown}
    />
  );
};

// Wire up the React component to the Redux store
export default connect(['regions', RegionsStore])(TailwindMultipleRegionEditor);
